import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, Users, ShoppingCart, Target, DollarSign,
  Plus, X, Calendar, Loader2, RefreshCw, AlertCircle, Trash2,
  BarChart3, ArrowRight, Zap, Award, PieChart, Edit2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';
import { toast } from 'sonner';

const AdminFunnels = () => {
  const [loading, setLoading] = useState(true);
  const [funnelData, setFunnelData] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [adSpendList, setAdSpendList] = useState([]);
  const [showAdSpendModal, setShowAdSpendModal] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('funnels');
  
  const [newAdSpend, setNewAdSpend] = useState({
    date: new Date().toISOString().split('T')[0],
    channel: 'facebook',
    campaign: '',
    amount: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [funnelsRes, metricsRes, adSpendRes] = await Promise.all([
        api.get('/marketing/funnels'),
        api.get('/marketing/metrics/cac-ltv'),
        api.get('/marketing/ad-spend')
      ]);
      setFunnelData(funnelsRes.data);
      setMetricsData(metricsRes.data);
      setAdSpendList(adSpendRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const createAdSpend = async () => {
    if (!newAdSpend.amount || parseFloat(newAdSpend.amount) <= 0) {
      toast.error('Introduce un importe válido');
      return;
    }
    try {
      await api.post('/marketing/ad-spend', {
        ...newAdSpend,
        amount: parseFloat(newAdSpend.amount)
      });
      toast.success('Gasto registrado');
      setShowAdSpendModal(false);
      setNewAdSpend({
        date: new Date().toISOString().split('T')[0],
        channel: 'facebook',
        campaign: '',
        amount: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Error al registrar gasto');
    }
  };

  const deleteAdSpend = async (spendId) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try {
      await api.delete(`/marketing/ad-spend/${spendId}`);
      toast.success('Gasto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'excellent': return 'text-emerald-600 bg-emerald-50';
      case 'good': return 'text-blue-600 bg-blue-50';
      case 'needs_improvement': return 'text-amber-600 bg-amber-50';
      case 'critical': return 'text-red-600 bg-red-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const getHealthLabel = (health) => {
    switch (health) {
      case 'excellent': return 'Excelente';
      case 'good': return 'Bueno';
      case 'needs_improvement': return 'A mejorar';
      case 'critical': return 'Crítico';
      default: return 'Sin datos';
    }
  };

  if (loading) {
    return (
      <div className="p-6 min-h-[40vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="funnels-tab">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-slate-100 pb-2">
        <button
          onClick={() => setActiveSubTab('funnels')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeSubTab === 'funnels'
              ? 'bg-emerald-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          Embudos
        </button>
        <button
          onClick={() => setActiveSubTab('metrics')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeSubTab === 'metrics'
              ? 'bg-emerald-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          CAC / LTV
        </button>
        <button
          onClick={() => setActiveSubTab('adspend')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeSubTab === 'adspend'
              ? 'bg-emerald-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <DollarSign className="w-4 h-4" />
          Gastos Ads
        </button>
      </div>

      {/* ========== FUNNELS TAB ========== */}
      {activeSubTab === 'funnels' && funnelData && (
        <div className="space-y-6">
          {/* Main KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400 uppercase">Ingresos Totales</span>
                <DollarSign className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-2xl font-semibold text-emerald-600">
                {formatCurrency(funnelData.main_funnel?.revenue)}
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400 uppercase">Clientes</span>
                <Users className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-2xl font-semibold text-slate-900">
                {funnelData.metrics?.total_customers || 0}
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400 uppercase">Conv. Visitante→Lead</span>
                <Target className="w-4 h-4 text-purple-500" />
              </div>
              <p className="text-2xl font-semibold text-purple-600">
                {funnelData.metrics?.conversion_visitor_to_lead || 0}%
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400 uppercase">Conv. Lead→Cliente</span>
                <ShoppingCart className="w-4 h-4 text-amber-500" />
              </div>
              <p className="text-2xl font-semibold text-amber-600">
                {funnelData.metrics?.conversion_lead_to_customer || 0}%
              </p>
            </div>
          </div>

          {/* Main Funnel */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Embudo Principal
            </h3>
            
            <div className="space-y-4">
              {(funnelData.main_funnel?.stages || []).map((stage, i) => (
                <div key={i} className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                        i === 0 ? 'bg-blue-500' :
                        i === 1 ? 'bg-purple-500' :
                        i === 2 ? 'bg-amber-500' :
                        'bg-emerald-500'
                      }`}>
                        {i + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{stage.name}</p>
                        <p className="text-sm text-slate-500">{stage.count} personas</p>
                      </div>
                    </div>
                    <span className={`text-lg font-semibold ${
                      stage.percentage >= 50 ? 'text-emerald-600' :
                      stage.percentage >= 20 ? 'text-amber-600' :
                      'text-red-600'
                    }`}>
                      {stage.percentage}%
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        i === 0 ? 'bg-blue-500' :
                        i === 1 ? 'bg-purple-500' :
                        i === 2 ? 'bg-amber-500' :
                        'bg-emerald-500'
                      }`}
                      style={{ width: `${stage.percentage}%` }}
                    />
                  </div>
                  {i < (funnelData.main_funnel?.stages?.length || 0) - 1 && (
                    <div className="absolute left-5 top-12 h-4 w-0.5 bg-slate-200" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Ebook Funnel */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-500" />
              {funnelData.ebook_funnel?.name || 'Embudo Ebook'}
            </h3>
            
            <div className="flex items-center justify-between gap-4 flex-wrap">
              {(funnelData.ebook_funnel?.stages || []).map((stage, i) => (
                <React.Fragment key={i}>
                  <div className="text-center flex-1 min-w-[120px]">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-xl ${
                      i === 0 ? 'bg-purple-500' :
                      i === 1 ? 'bg-indigo-500' :
                      'bg-emerald-500'
                    }`}>
                      {stage.count}
                    </div>
                    <p className="text-sm font-medium text-slate-700">{stage.name}</p>
                    <p className="text-xs text-slate-500">{stage.percentage}%</p>
                  </div>
                  {i < (funnelData.ebook_funnel?.stages?.length || 0) - 1 && (
                    <ArrowRight className="w-6 h-6 text-slate-300 flex-shrink-0" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ========== CAC/LTV TAB ========== */}
      {activeSubTab === 'metrics' && metricsData && (
        <div className="space-y-6">
          {/* Main Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* CAC Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-900">CAC</h4>
                <TrendingDown className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-3xl font-bold text-slate-900 mb-2">
                {formatCurrency(metricsData.cac?.value)}
              </p>
              <p className="text-sm text-slate-500">Coste de Adquisición de Cliente</p>
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Gasto en Ads</span>
                  <span className="font-medium">{formatCurrency(metricsData.cac?.ad_spend)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Nuevos Clientes</span>
                  <span className="font-medium">{metricsData.cac?.new_customers || 0}</span>
                </div>
              </div>
            </div>

            {/* LTV Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-900">LTV</h4>
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              <p className="text-3xl font-bold text-emerald-600 mb-2">
                {formatCurrency(metricsData.ltv?.value)}
              </p>
              <p className="text-sm text-slate-500">Valor de Vida del Cliente</p>
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Ingresos Totales</span>
                  <span className="font-medium">{formatCurrency(metricsData.ltv?.total_revenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Total Clientes</span>
                  <span className="font-medium">{metricsData.ltv?.total_customers || 0}</span>
                </div>
              </div>
            </div>

            {/* LTV:CAC Ratio */}
            <div className={`rounded-xl p-6 shadow-sm border ${getHealthColor(metricsData.health)}`}>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold">Ratio LTV:CAC</h4>
                <Award className="w-5 h-5" />
              </div>
              <p className="text-3xl font-bold mb-2">
                {metricsData.ltv_cac_ratio || 0}:1
              </p>
              <p className="text-sm opacity-80">
                {getHealthLabel(metricsData.health)}
              </p>
              <div className="mt-4 pt-4 border-t border-current/10 text-sm opacity-80">
                <p>≥3:1 Excelente</p>
                <p>≥2:1 Bueno</p>
                <p>≥1:1 A mejorar</p>
                <p>&lt;1:1 Crítico</p>
              </div>
            </div>
          </div>

          {/* Ad Spend by Channel */}
          {metricsData.cac?.by_channel?.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-blue-500" />
                Gasto por Canal
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {metricsData.cac.by_channel.map((ch, i) => (
                  <div key={i} className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 capitalize">{ch.channel}</p>
                    <p className="text-xl font-semibold text-slate-900">{formatCurrency(ch.spend)}</p>
                    <p className="text-xs text-slate-400">{ch.entries} registros</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Customers */}
          {metricsData.top_customers?.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Top Clientes por LTV
              </h4>
              <div className="space-y-3">
                {metricsData.top_customers.map((customer, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                        i === 0 ? 'bg-amber-500' :
                        i === 1 ? 'bg-slate-400' :
                        i === 2 ? 'bg-amber-700' :
                        'bg-slate-300'
                      }`}>
                        {i + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{customer.email}</p>
                        <p className="text-xs text-slate-500">{customer.purchases_count} compras</p>
                      </div>
                    </div>
                    <span className="font-semibold text-emerald-600">{formatCurrency(customer.total_spent)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== AD SPEND TAB ========== */}
      {activeSubTab === 'adspend' && (
        <div className="space-y-4">
          {/* Actions */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-slate-900">Gastos en Publicidad</h3>
              <p className="text-sm text-slate-500">Registra tus gastos de ads para calcular el CAC</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
              <Button size="sm" onClick={() => setShowAdSpendModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Registrar Gasto
              </Button>
            </div>
          </div>

          {/* Ad Spend Table */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Fecha</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Canal</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Campaña</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Importe</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Notas</th>
                    <th className="text-right p-4 text-xs font-medium text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {adSpendList.map(spend => (
                    <tr key={spend.id} className="hover:bg-slate-50">
                      <td className="p-4 text-sm text-slate-900">{formatDate(spend.date)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          spend.channel === 'facebook' ? 'bg-blue-100 text-blue-700' :
                          spend.channel === 'google' ? 'bg-red-100 text-red-700' :
                          spend.channel === 'instagram' ? 'bg-pink-100 text-pink-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {spend.channel}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-slate-600">{spend.campaign || '-'}</td>
                      <td className="p-4 text-sm font-semibold text-slate-900">{formatCurrency(spend.amount)}</td>
                      <td className="p-4 text-sm text-slate-500 max-w-xs truncate">{spend.notes || '-'}</td>
                      <td className="p-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteAdSpend(spend.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {adSpendList.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-400">
                        <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No hay gastos registrados</p>
                        <p className="text-sm mt-1">Registra tus gastos de publicidad para calcular el CAC</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========== ADD AD SPEND MODAL ========== */}
      {showAdSpendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-slate-100">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-slate-900">Registrar Gasto de Ads</h3>
                <button onClick={() => setShowAdSpendModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha *</label>
                <Input
                  type="date"
                  value={newAdSpend.date}
                  onChange={(e) => setNewAdSpend({ ...newAdSpend, date: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Canal *</label>
                <select
                  value={newAdSpend.channel}
                  onChange={(e) => setNewAdSpend({ ...newAdSpend, channel: e.target.value })}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="facebook">Facebook Ads</option>
                  <option value="instagram">Instagram Ads</option>
                  <option value="google">Google Ads</option>
                  <option value="youtube">YouTube Ads</option>
                  <option value="linkedin">LinkedIn Ads</option>
                  <option value="other">Otro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Campaña</label>
                <Input
                  value={newAdSpend.campaign}
                  onChange={(e) => setNewAdSpend({ ...newAdSpend, campaign: e.target.value })}
                  placeholder="ej: Ebook Febrero 2025"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Importe (€) *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={newAdSpend.amount}
                  onChange={(e) => setNewAdSpend({ ...newAdSpend, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
                <Input
                  value={newAdSpend.notes}
                  onChange={(e) => setNewAdSpend({ ...newAdSpend, notes: e.target.value })}
                  placeholder="Notas adicionales..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-slate-100 flex gap-3">
              <Button variant="outline" onClick={() => setShowAdSpendModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={createAdSpend} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Registrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFunnels;
